import {
  Users, KeyRound, Lock, Shield, Library, Palette, List, Bell, Mail, FileCode, Sparkles,
  Activity, ScrollText, SlidersHorizontal, Plug,
} from 'lucide-react';

/**
 * The administration area, grouped.
 *
 * Thirteen settings pages used to sit as thirteen sidebar entries. They are
 * now five groups; each group is one sidebar entry and the pages inside it are
 * tabs. The routes have not moved — the Help page, the dashboard quick links
 * and the setup wizard all deep-link to `/admin/...` and still land on the
 * same page, now with its group's tabs above it.
 */
export interface AdminNavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  tone?: string;
}

export interface AdminNavGroup {
  id: string;
  label: string;
  /** One line under the group label on the tab strip. */
  blurb: string;
  icon: React.ComponentType<{ className?: string }>;
  tone: string;
  items: AdminNavItem[];
}

export const ADMIN_GROUPS: AdminNavGroup[] = [
  {
    id: 'access', label: 'Users & Access', icon: Users, tone: 'text-sky-500',
    blurb: 'Who can sign in, how they sign in, and what a session is allowed to do.',
    items: [
      { to: '/admin/users',    label: 'Users',    icon: Users,    tone: 'text-sky-500' },
      { to: '/admin/sso',      label: 'SSO',      icon: KeyRound, tone: 'text-emerald-500' },
      { to: '/admin/security', label: 'Security', icon: Lock,     tone: 'text-rose-500' },
    ],
  },
  {
    id: 'frameworks', label: 'Framework Admin', icon: Shield, tone: 'text-violet-500',
    blurb: 'The frameworks the platform assesses against, and keeping them current.',
    items: [
      { to: '/admin/frameworks',        label: 'Frameworks',     icon: Shield,  tone: 'text-violet-500' },
      { to: '/admin/framework-library', label: 'Library & Sync', icon: Library, tone: 'text-amber-500' },
    ],
  },
  {
    id: 'platform', label: 'Platform', icon: SlidersHorizontal, tone: 'text-fuchsia-500',
    blurb: 'How the platform looks, the lists it offers, and when it speaks up.',
    items: [
      { to: '/admin/branding',      label: 'Branding',      icon: Palette, tone: 'text-fuchsia-500' },
      { to: '/admin/lookups',       label: 'Lookup Lists',  icon: List,    tone: 'text-lime-600' },
      { to: '/admin/notifications', label: 'Notifications', icon: Bell,    tone: 'text-amber-500' },
    ],
  },
  {
    id: 'integrations', label: 'Integrations', icon: Plug, tone: 'text-blue-500',
    blurb: 'Outside services the platform relies on: mail, the messages it sends, and AI.',
    items: [
      { to: '/admin/smtp',            label: 'SMTP',            icon: Mail,     tone: 'text-blue-500' },
      { to: '/admin/email-templates', label: 'Email Templates', icon: FileCode, tone: 'text-cyan-500' },
      { to: '/admin/ai',              label: 'AI',              icon: Sparkles, tone: 'text-purple-500' },
    ],
  },
  {
    id: 'system', label: 'System', icon: Activity, tone: 'text-green-500',
    blurb: 'Is it healthy, and who did what.',
    items: [
      { to: '/admin/health',     label: 'Health',     icon: Activity,   tone: 'text-green-500' },
      { to: '/admin/audit-logs', label: 'Audit Logs', icon: ScrollText, tone: 'text-slate-400' },
    ],
  },
];

/** The group a path belongs to, if it is one of the grouped settings pages. */
export function adminGroupFor(pathname: string): AdminNavGroup | undefined {
  return ADMIN_GROUPS.find(g => g.items.some(i => pathname === i.to || pathname.startsWith(i.to + '/')));
}
